import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Loader2 } from 'lucide-react';
import { ProductCard } from '@/components/products/ProductCard';
import { Button } from '@/components/ui/button';
import { useNewArrivals, useHomeStats } from '@/hooks/useApi.js';

export function NewArrivals() {
  const { data: newArrivalsData, isLoading, error } = useNewArrivals(0, 4, 30);
  const { data: stats } = useHomeStats();
  const newArrivals = newArrivalsData?.content || [];

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-discount/10 text-discount px-4 py-2 rounded-full mb-6">
                <Sparkles className="h-4 w-4" />
                <span className="text-sm font-medium font-sans">Just Dropped</span>
              </div>
              
              <h2 className="section-heading font-serif mb-4">
                New Arrivals
              </h2>
              <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto lg:mx-0 font-sans">
                Explore the latest additions to our collection. Fresh styles, premium quality, designed just for you.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/new-arrivals">
                  <Button size="lg" className="gap-2 bg-gradient-warm hover:opacity-90 font-sans">
                    Explore Collection
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="flex items-center justify-center lg:justify-start gap-8 mt-10">
                <div className="text-center lg:text-left">
                  <p className="text-3xl font-bold text-foreground font-serif">
                    {newArrivalsData?.page?.totalElements || 0}+
                  </p>
                  <p className="text-sm text-muted-foreground font-sans">New Items</p>
                </div>
                <div className="h-12 w-px bg-border" />
                <div className="text-center lg:text-left">
                  <p className="text-3xl font-bold text-foreground font-serif">
                    {stats?.averageRating ? stats.averageRating.toFixed(1) : '0.0'}
                  </p>
                  <p className="text-sm text-muted-foreground font-sans">Avg Rating</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Products */}
          <div className="lg:col-span-3">
            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2">Loading new arrivals...</span>
              </div>
            ) : error ? (
              <div className="text-center h-64 flex items-center justify-center">
                <div>
                  <p className="text-muted-foreground mb-4">Failed to load new arrivals</p>
                  <Button onClick={() => window.location.reload()}>Try Again</Button>
                </div>
              </div>
            ) : newArrivals.length === 0 ? (
              <div className="text-center h-64 flex items-center justify-center">
                <div>
                  <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No new arrivals yet</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 md:gap-6">
                {newArrivals.map((product, index) => (
                  <motion.div
                    key={product.productId}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <ProductCard product={product} />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}